import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, ArrowRight } from "@phosphor-icons/react";

export default function QuoteCalculator() {
  const [form, setForm] = useState({
    origin: "",
    destination: "",
    weight_kg: 1,
    service_type: "Domestic",
    speed: "Standard",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/quote", {
        ...form,
        weight_kg: parseFloat(form.weight_kg) || 0,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b-2 border-neutral-300 focus:border-signal outline-none py-2 font-sans text-obsidian placeholder:text-neutral-400 transition-colors";
  const selectCls =
    "w-full bg-transparent border-b-2 border-neutral-300 focus:border-signal outline-none py-2 font-sans text-obsidian transition-colors";

  return (
    <div className="grid md:grid-cols-2 gap-0 border-2 border-obsidian bg-white shadow-brutal">
      <form onSubmit={submit} className="p-8 md:p-10 space-y-6" data-testid="quote-form">
        <div className="flex items-center gap-2 text-signal">
          <Calculator weight="bold" size={22} />
          <span className="font-mono text-xs tracking-widest uppercase">Rate Estimator</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Origin</label>
            <input data-testid="quote-origin" className={inputCls} required value={form.origin}
              onChange={(e) => set("origin", e.target.value)} placeholder="Hyderabad" />
          </div>
          <div>
            <label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Destination</label>
            <input data-testid="quote-destination" className={inputCls} required value={form.destination}
              onChange={(e) => set("destination", e.target.value)} placeholder="Dubai" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Weight (kg)</label>
            <input data-testid="quote-weight" type="number" min="0.1" step="0.1" className={inputCls} required
              value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} />
          </div>
          <div>
            <label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Service</label>
            <select data-testid="quote-service" className={selectCls} value={form.service_type}
              onChange={(e) => set("service_type", e.target.value)}>
              <option>Domestic</option>
              <option>International</option>
            </select>
          </div>
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Speed</label>
          <div className="flex gap-3 mt-2">
            {["Standard", "Express"].map((s) => (
              <button type="button" key={s} data-testid={`quote-speed-${s.toLowerCase()}`}
                onClick={() => set("speed", s)}
                className={`px-5 py-2 border-2 font-medium text-sm transition-colors ${
                  form.speed === s ? "bg-signal text-white border-signal" : "bg-white border-neutral-300 text-obsidian hover:border-obsidian"
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <button data-testid="quote-submit" disabled={loading}
          className="group flex items-center gap-2 bg-signal text-white font-medium px-6 py-3 hover:bg-signal-hover transition-colors disabled:opacity-60">
          {loading ? "Calculating..." : "Calculate Rate"}
          <ArrowRight weight="bold" className="group-hover:translate-x-1 transition-transform" />
        </button>
      </form>

      <div className="bg-obsidian text-stark p-8 md:p-10 flex flex-col justify-center" data-testid="quote-result">
        {result ? (
          <div>
            <p className="font-mono text-xs tracking-widest uppercase text-neutral-400">Estimated Cost</p>
            <p className="font-display font-semibold text-6xl mt-2 text-signal">
              ₹{result.amount.toLocaleString()}
            </p>
            <div className="mt-8 space-y-3 font-mono text-sm">
              <div className="flex justify-between border-b border-neutral-800 pb-2">
                <span className="text-neutral-400">Route</span><span>{result.origin} → {result.destination}</span>
              </div>
              <div className="flex justify-between border-b border-neutral-800 pb-2">
                <span className="text-neutral-400">Service</span><span>{result.service_type} · {result.speed}</span>
              </div>
              <div className="flex justify-between border-b border-neutral-800 pb-2">
                <span className="text-neutral-400">Weight</span><span>{result.weight_kg} kg</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Delivery in</span><span className="text-signal">~{result.estimated_days} days</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-neutral-500">
            <p className="font-display text-3xl text-neutral-700 leading-tight">Your instant quote appears here.</p>
            <p className="font-sans mt-4 text-neutral-500">Fill the details and hit calculate. No sign-up needed.</p>
          </div>
        )}
      </div>
    </div>
  );
}
